import { useState } from 'react';
import type { DataFeedGrant } from '@catalyst-psa/shared';
import { API_BASE_PATH, DEFAULT_PAGE_SIZE, ODATA_BASE_PATH } from '@catalyst-psa/shared';

import { env } from '../../config/env.js';
import { useAuth } from '../../auth/authContext.js';
import { PageHeader } from '../../components/layout/PageHeader.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.js';
import { DataState } from '../../components/ui/DataState.js';
import { Icon } from '../../components/ui/Icon.js';
import { Pagination } from '../../components/ui/Pagination.js';
import { formatDate } from '../../utils/date.js';
import { DataFeedGrantFormModal } from './DataFeedGrantFormModal.js';
import { useDataFeedGrants, useDeleteDataFeedGrant, useRevokeDataFeedGrant } from './api.js';

interface StatusDisplay {
  label: string;
  tone: 'green' | 'gray' | 'amber' | 'red';
}

function statusOf(grant: DataFeedGrant): StatusDisplay {
  if (grant.revokedAt) return { label: 'Revoked', tone: 'red' };
  if (grant.expiresAt && new Date(grant.expiresAt).getTime() < Date.now()) {
    return { label: 'Expired', tone: 'amber' };
  }
  return grant.isActive ? { label: 'Active', tone: 'green' } : { label: 'Inactive', tone: 'gray' };
}

function CopyButton({ value }: { value: string }): JSX.Element {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => {
        void navigator.clipboard.writeText(value).then(() => {
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1500);
        });
      }}
    >
      {copied ? 'Copied' : 'Copy'}
    </Button>
  );
}

/** How to connect Excel / Power BI to the read-only OData feed. */
function ConnectionCard(): JSX.Element {
  const origin = (env.apiBaseUrl || window.location.origin).replace(/\/$/, '');
  const feedUrl = `${origin}${API_BASE_PATH}${ODATA_BASE_PATH}`;

  return (
    <div className="mb-6 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <h2 className="text-sm font-semibold text-slate-900">Connecting Excel to Catalyst PSA</h2>
      <p className="mt-0.5 text-xs text-slate-500">
        A read-only, always-refreshable data connection. No sign-in to PSA required — Excel stores
        the key securely and refreshes on demand.
      </p>

      <div className="mt-4">
        <p className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">
          OData feed URL
        </p>
        <div className="flex items-center gap-2">
          <code className="flex-1 break-all rounded-lg border border-slate-200 bg-slate-50 px-2 py-1 font-mono text-xs text-slate-800">
            {feedUrl}
          </code>
          <CopyButton value={feedUrl} />
        </div>
      </div>

      <ol className="mt-4 list-decimal space-y-1 pl-5 text-xs text-slate-600">
        <li>Grant a person access below and copy their one-time key.</li>
        <li>
          In Excel: <strong>Data → Get Data → From Other Sources → From OData Feed</strong>.
        </li>
        <li>Paste the feed URL above and click OK.</li>
        <li>
          When prompted, choose <strong>Basic</strong> authentication. Enter any username and paste
          the key as the <strong>password</strong>.
        </li>
        <li>
          Pick the tables to load — <strong>Opportunities</strong>, <strong>Projects</strong>,{' '}
          <strong>Agreements</strong>, and <strong>Hours</strong> mirror the legacy ConnectWise
          Excel extracts, plus Customers, Tickets, Time Entries, and Invoices. Use{' '}
          <strong>Refresh All</strong> anytime to pull the latest data.
        </li>
      </ol>
      <p className="mt-3 text-xs text-slate-500">
        A person only sees the tables their permissions allow. Revoke a key here to cut off access
        immediately.
      </p>
    </div>
  );
}

export function ExcelAccessPage(): JSX.Element {
  const { isAdmin } = useAuth();

  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [revoking, setRevoking] = useState<DataFeedGrant | null>(null);
  const [deleting, setDeleting] = useState<DataFeedGrant | null>(null);

  const query = useDataFeedGrants({ page, pageSize: DEFAULT_PAGE_SIZE });
  const revokeMutation = useRevokeDataFeedGrant();
  const deleteMutation = useDeleteDataFeedGrant();

  const grants = query.data?.data ?? [];
  const pageInfo = query.data?.page;

  const handleRevoke = (): void => {
    if (!revoking) return;
    revokeMutation.mutate(revoking.id, { onSuccess: () => setRevoking(null) });
  };
  const handleDelete = (): void => {
    if (!deleting) return;
    deleteMutation.mutate(deleting.id, { onSuccess: () => setDeleting(null) });
  };

  return (
    <div>
      <PageHeader
        title="Excel Access"
        description="Give individuals a read-only, refreshable Excel/Power BI connection to PSA data."
        actions={
          isAdmin ? (
            <Button onClick={() => setModalOpen(true)}>
              <Icon name="apiKeys" className="h-4 w-4" />
              Grant access
            </Button>
          ) : undefined
        }
      />

      <ConnectionCard />

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3 font-medium">Person</th>
              <th className="px-4 py-3 font-medium">Email</th>
              <th className="px-4 py-3 font-medium">Key prefix</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Last used</th>
              <th className="px-4 py-3 font-medium">Expires</th>
              <th className="px-4 py-3 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            <DataState
              colSpan={7}
              isLoading={query.isLoading}
              isError={query.isError}
              error={query.error}
              onRetry={() => void query.refetch()}
              isEmpty={grants.length === 0}
              emptyTitle="No one has Excel access yet"
              emptyDescription="Grant access to give someone a read-only Excel connection."
              emptyAction={
                isAdmin ? (
                  <Button onClick={() => setModalOpen(true)}>Grant access</Button>
                ) : undefined
              }
            >
              {grants.map((grant) => {
                const status = statusOf(grant);
                const canRevoke = isAdmin && !grant.revokedAt;
                return (
                  <tr key={grant.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium text-slate-900">{grant.personName}</td>
                    <td className="px-4 py-3 text-slate-600">{grant.email || '—'}</td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-600">
                      {grant.keyPrefix}
                    </td>
                    <td className="px-4 py-3">
                      <Badge tone={status.tone}>{status.label}</Badge>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{formatDate(grant.lastUsedAt)}</td>
                    <td className="px-4 py-3 text-slate-600">{formatDate(grant.expiresAt)}</td>
                    <td className="px-4 py-3 text-right">
                      {isAdmin ? (
                        <div className="flex justify-end gap-2">
                          {canRevoke ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-amber-600 hover:bg-amber-50"
                              onClick={() => setRevoking(grant)}
                            >
                              Revoke
                            </Button>
                          ) : null}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-rose-600 hover:bg-rose-50"
                            onClick={() => setDeleting(grant)}
                          >
                            Delete
                          </Button>
                        </div>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </DataState>
          </tbody>
        </table>

        {pageInfo && grants.length > 0 ? (
          <Pagination
            page={pageInfo.page}
            pageSize={pageInfo.pageSize}
            totalItems={pageInfo.totalItems}
            totalPages={pageInfo.totalPages}
            onPageChange={setPage}
          />
        ) : null}
      </div>

      <DataFeedGrantFormModal isOpen={modalOpen} onClose={() => setModalOpen(false)} />

      <ConfirmDialog
        isOpen={revoking !== null}
        title="Revoke Excel access"
        message={`Revoke access for "${revoking?.personName ?? ''}"? Their connection will stop working immediately.`}
        confirmLabel="Revoke"
        destructive
        isConfirming={revokeMutation.isPending}
        onConfirm={handleRevoke}
        onCancel={() => setRevoking(null)}
      />

      <ConfirmDialog
        isOpen={deleting !== null}
        title="Delete Excel access"
        message={`Delete access for "${deleting?.personName ?? ''}"? This permanently removes the credential.`}
        confirmLabel="Delete"
        destructive
        isConfirming={deleteMutation.isPending}
        onConfirm={handleDelete}
        onCancel={() => setDeleting(null)}
      />
    </div>
  );
}
